<template>
  <div class="html-material">
    <div class="material-controls">
      <div class="container visible-pc-only">
        <div class="lesson-description">
          <p class="rank">{{ pcRankDescription }}</p>
          <p class="lesson">{{ pcLessonDescription }}</p>
        </div>
        <MaterialControlsZoom />
        <div>
          <el-button @click="downloadHtml" plain>{{ $t('htmlMaterial.downloadHtml') }}</el-button>
        </div>
      </div>

      <div class="container visible-sp-only">
        <p>{{ getProgramName() }}</p>
        <el-button @click="downloadHtml">{{ $t('htmlMaterial.downloadHtml') }}</el-button>
      </div>
    </div>

    <div class="lesson-info visible-sp-only">
      <p class="rank">{{ spLevelRank }}</p>
      <div class="lesson-title">
        <p>Lesson {{ lessonNo }}</p>
        <p class="text-bold">{{ selectedLesson.title }}</p>
      </div>
    </div>

    <div class="html-wrapper">
      <el-aside width="200px" class="lesson-selection">
        <perfect-scrollbar class="scroll">
          <el-menu :default-openeds="['1']" :default-active="activeIndex">
            <el-submenu index="1">
              <template slot="title"><i class="el-icon-tickets"></i>Lessons</template>
              <el-menu-item-group>
                <template v-for="i in htmlTrainerMaterials.length">
                  <el-menu-item :key="i" :index="'lesson-' + i" v-scroll-to="'#lesson-' + i"
                    >Lesson {{ i }}
                  </el-menu-item>
                  <el-divider :key="i" />
                </template>
              </el-menu-item-group>
            </el-submenu>
          </el-menu>
        </perfect-scrollbar>
      </el-aside>

      <perfect-scrollbar
        :class="['html-inner', { 'zoom-active': isZoomActive }]"
        :style="mobileHeight"
        v-dragscroll="true"
        ref="materialScroll"
        @scroll="scrollSpy"
      >
        <div class="content-wrapper" :style="materialWidth">
          <template v-for="(material, key) in htmlTrainerMaterials">
            <div class="per-material" :key="key" :id="`lesson-${material.no}`">
              <div v-html="setStyleTag(material.css)" />
              <div class="html-content" v-dompurify-html="material.html" />
            </div>
          </template>
        </div>
      </perfect-scrollbar>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { dragscroll } from 'vue-dragscroll';
import Vue from 'vue';
import VueScrollTo from 'vue-scrollto';

import MaterialControlsZoom from '@/components/Lesson/Material/MaterialControlsZoom';
import materialMixin from '@/mixins/material';

Vue.use(VueScrollTo, {
  container: '.html-inner',
  duration: 200
});

export default {
  directives: { dragscroll },
  components: {
    MaterialControlsZoom
  },
  mixins: [materialMixin],
  data() {
    return {
      lessonNo: 1,
      activeIndex: 'lesson-1',
      scrollSpy: this.$_.debounce(this.setActiveMaterialOnScroll, 50)
    };
  },
  mounted() {
    this.showHtmlContent(true);
    this.setMaterialLevel();
    this.initializeRevamp();
  },
  computed: {
    ...mapState('material', ['pdfHtmlZoom']),
    ...mapState('htmlMaterial', ['htmlTrainerMaterials']),
    ...mapGetters('htmlMaterial', ['isStudent']),

    isZoomActive() {
      return this.pdfHtmlZoom !== 1;
    },

    zoomIcon() {
      const icon = this.isMaterialFullScreen ? 'text_zoomout_sp@2x.png' : 'text_zoom_sp@2x.png';
      return require(`@/assets/img/${icon}`);
    },

    materialWidth() {
      const htmlMaterialWidth = 936 * this.scalePercentage - 32;
      const pdfHtmlWidth = htmlMaterialWidth * this.pdfHtmlZoom;
      return { width: pdfHtmlWidth + 'px' };
    },

    pcRankDescription() {
      const revamp = true;
      return this.formatHtmlMaterialRankLabel(
        this.selectedLesson.level,
        this.selectedLesson.rank,
        revamp,
        this.isStudent
      );
    },

    pcLessonDescription() {
      return this.formatHtmlMaterialLessonLabel(this.lessonNo, this.selectedLesson.title);
    },

    spLevelRank() {
      return `${this.selectedLesson.level} ${this.selectedLesson.rank}`;
    },

    selectedLesson() {
      return this.htmlTrainerMaterials.find((key) => key.no === this.lessonNo);
    }
  },
  methods: {
    ...mapMutations('material', ['showHtmlContent']),

    setStyleTag(css) {
      return `<style type="text/css">${css}</style>`;
    },

    setMaterialLevel() {
      const htmlMaterial = document.querySelector('.html-lesson-wrapper');
      if (htmlMaterial) {
        htmlMaterial.classList.add(`level-${this.htmlLevel}`);
        htmlMaterial.classList.add(`lesson-${this.htmlLessonId}`);
      }
    },

    setActiveMaterialOnScroll() {
      const scrollBar = this.$refs.materialScroll;
      const scrollY = scrollBar.$el.scrollTop;
      const lessons = document.querySelectorAll('.content-wrapper .per-material');

      if (!lessons.length) return;

      for (let i = 0; i < lessons.length; i++) {
        const lessonTop = lessons[i].offsetTop - i * 5;
        if (lessonTop > scrollY) {
          this.setLessonInfo(i);
          break;
        }
        this.setLessonInfo(lessons.length);
      }
    },

    setLessonInfo(i) {
      this.lessonNo = i;
      this.activeIndex = 'lesson-' + i;
    },

    print() {
      document.title = this.spLevelRank;
      window.print();
    },

    downloadHtml() {
      const zipFile = this.selectedLesson.html_zip;
      fetch(zipFile)
        .then((response) => response.blob())
        .then((blob) => {
          const link = document.createElement('a');
          const fileName = this.spLevelRank;
          link.setAttribute('download', fileName + '.zip');
          link.setAttribute('href', URL.createObjectURL(blob));
          link.click();
          window.URL.revokeObjectURL(blob);
        });
    }
  }
};
</script>

<style src="@/assets/css/pc/html_material.css"></style>
<style scoped src="@/assets/css/sp/html_material.css"></style>
<style scoped>
.html-content {
  transform-origin: top left;
  background-color: #ffffff;
  transform: scale(var(--revamp-scale));
  width: 174%;
}
.html-material .material-controls .container {
  width: calc(
    var(--revamp-wrapper-width) * var(--initial-revamp-scale) + var(--revamp-lesson-menu-width)
  ) !important;
}
.html-wrapper {
  display: flex;
  flex-direction: row;
  width: calc(
    var(--revamp-wrapper-width) * var(--initial-revamp-scale) + var(--revamp-lesson-menu-width)
  ) !important;
}
.content-wrapper {
  overflow: hidden;
}
.content-wrapper .per-material:not(:last-child) {
  margin-bottom: 40px;
}
.lesson-selection .scroll {
  height: calc(100vh - 104px);
}
.lesson-selection >>> .el-submenu__title {
  pointer-events: none !important;
  border-bottom: 1px solid #ebeef1;
}
.lesson-selection >>> .el-menu {
  min-height: calc(100vh - 104px);
}
.lesson-selection >>> .el-menu-item:focus:not(.is-active) {
  background: none;
}
.lesson-selection >>> .el-menu-item.is-active {
  background-color: #ecf5ff;
  color: #303133;
}
.lesson-selection >>> .el-menu .el-divider--horizontal {
  margin: 0px;
  background: #ebeef1;
  width: 85%;
  height: 0.5px;
  margin: 0 auto;
  margin-top: -1px;
  margin-bottom: -1px;
}
.lesson-selection >>> .el-submenu__icon-arrow {
  display: none;
}

@media screen and (max-width: 959px) {
  .html-wrapper {
    flex-direction: column;
    width: 100vw !important;
  }
  .html-wrapper >>> .el-aside {
    display: none;
  }
  .content-wrapper,
  .html-material .material-controls .container {
    width: 100vw !important;
  }
  .html-content {
    transform: unset;
    width: 100%;
    margin-right: 0px !important;
    margin-bottom: 0px !important;
  }
}
</style>
